import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { TrendingUp, Clock, ArrowUpRight, Newspaper } from "lucide-react";

interface TrendingNewsSectionProps {
  featured: any;
  secondary: any[];
  isLoading: boolean;
}

export function TrendingNewsSection({ featured, secondary, isLoading }: TrendingNewsSectionProps) {
  const hasData = featured || secondary.length > 0;

  if (!hasData && !isLoading) return null;

  return (
    <section className="border-b border-border bg-neutral-50/50" aria-labelledby="trending-news-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Trending"
          icon={TrendingUp}
          title="Trending HR News"
          href="/news"
        />
        {isLoading ? (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2"><SectionLoader variant="hero" /></div>
            <div className="space-y-4"><SectionLoader variant="list" count={3} /></div>
          </div>
        ) : hasData ? (
          <div className="grid gap-6 lg:grid-cols-3">
            {featured && (
              <div className="lg:col-span-2">
                <Link href={`/news/${featured.id}`} className="group block">
                  <div className="aspect-[16/9] bg-neutral-100 rounded-xl mb-5 overflow-hidden border border-border/50">
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground/20 bg-neutral-50">
                      <Newspaper className="h-16 w-16" />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{featured.readTime ?? "5 min read"}</span>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-heading font-semibold leading-snug text-foreground group-hover:text-primary transition-colors duration-200">
                      {featured.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                      {typeof featured.content === "string" ? featured.content.slice(0, 200) : ""}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
                      <span className="font-medium text-foreground">{featured.author}</span>
                      <span className="text-neutral-300">·</span>
                      <span>{featured.createdAt}</span>
                    </div>
                  </div>
                </Link>
              </div>
            )}
            <div className="space-y-4">
              {secondary.map((item: any) => (
                <Link key={item.id} href={`/news/${item.id}`} className="group block">
                  <div className="flex gap-4 items-start">
                    <span className="font-display text-2xl font-semibold text-neutral-200 shrink-0 tabular-nums leading-none pt-1">
                      {"0" + (secondary.indexOf(item) + 2)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-heading font-semibold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2">
                        {item.title}
                      </h4>
                      <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{item.author}</span>
                        <span className="text-neutral-300">·</span>
                        <span>{item.createdAt}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <EmptyState icon={TrendingUp} description="No trending news yet." />
        )}
      </div>
    </section>
  );
}